import React, { Fragment, useState, useEffect } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

const Filter = ({ column }) => {
  const [filterValue, setFilterValue] = useState('');

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
    column.setFilterValue(e.target.value);
  };

  return (
    <input
      type="text"
      value={filterValue}
      onChange={handleFilterChange}
      placeholder={`Search...`}
      className="form-control"
      style={{ width: '100%' }}
    />
  );
};

const TableContainerNoPagination = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  customPageIndex,
  onPageChange,
  onFilteredDataChange,
  globalFilter: externalGlobalFilter,
  onGlobalFilterChange,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  onRowClick,
}) => {
  const [columnFilters, setColumnFilters] = useState ([]);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: customPageIndex || 0,
    pageSize: customPageSize || 10
  });

  // Usar filtro externo si se proporciona, sino usar el interno
  const globalFilter = externalGlobalFilter !== undefined ? externalGlobalFilter : internalGlobalFilter;

  // Actualizar pageSize cuando cambia customPageSize
  useEffect(() => {
    if (customPageSize && customPageSize !== pagination.pageSize) {
      setPagination(prev => ({
        ...prev,
        pageSize: customPageSize,
        pageIndex: 0 // Resetear a la primera página
      }));
    }
  }, [customPageSize]);

  // Actualizar pageIndex cuando cambia customPageIndex
  useEffect(() => {
    if (customPageIndex !== undefined && customPageIndex !== pagination.pageIndex) {
      setPagination(prev => ({
        ...prev,
        pageIndex: customPageIndex
      }));
    }
  }, [customPageIndex]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      sorting,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: (value) => {
      if (onGlobalFilterChange) {
        onGlobalFilterChange(value);
      } else {
        setInternalGlobalFilter(value);
      }
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // IMPORTANTE: El orden importa - primero filtrar, luego ordenar, finalmente paginar
  });

  const {
    getHeaderGroups,
    getRowModel,
    getFilteredRowModel: getFilteredRows,
  } = table;

  // Resetear a la primera página cuando se ordena
  useEffect(() => {
    if (sorting.length > 0) {
      setPagination(prev => ({
        ...prev,
        pageIndex: 0
      }));
      if (onPageChange) {
        onPageChange(0);
      }
    }
  }, [sorting, onPageChange]);

  // Notificar al padre sobre el conteo de datos filtrados
  useEffect(() => {
    if (onFilteredDataChange) {
      const filteredRows = getFilteredRows();
      onFilteredDataChange(filteredRows.rows.length);
    }
  }, [globalFilter, data, onFilteredDataChange]);

  return (
    <Fragment>
      {isGlobalFilter && <CardBody className="border border-dashed border-end-0 border-start-0">
        <form>
          <Row>
            <Col sm={5}>
              <div className="search-box me-2 mb-2 d-inline-block col-12">
                <input
                  value={globalFilter}
                  onChange={(e) => {
                    const value = e.target.value || "";
                    if (onGlobalFilterChange) {
                      onGlobalFilterChange(value);
                    } else {
                      setInternalGlobalFilter(value);
                    }
                  }}
                  id="search-bar-0"
                  type="text"
                  className="form-control search /"
                  placeholder={SearchPlaceholder}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </Col>
          </Row>
        </form>
      </CardBody>}

      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={thClass}  {...{
                    onClick: header.column.getToggleSortingHandler(),
                  }}>
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: <i className="ri-arrow-down-s-line"></i>,
                          desc:  <i className="ri-arrow-up-s-line"></i>,
                        }
                        [header.column.getIsSorted()] ?? null}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row) => {
              return (
                <tr 
                  key={row.id}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  style={onRowClick ? { cursor: 'pointer' } : {}}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Fragment>
  );
};

export default TableContainerNoPagination;
